import { axiosDelete, axiosGet, axiosPatch, axiosPost } from '@/api/handlers';
import { useMutation, useQuery } from '@/api/hooks';
import { authenticationQueryKeyMap } from '@/features/authentication/_utils/query-keys';
import { queryKeyMap as projectQueryKeyMap } from '@/features/project/_utils/query-keys';
import { useInvalidateQuery } from '@/hooks';
import { BulkUpdateStatusBaseModel } from '@/utils';
import type { z } from 'zod';
import {
  CommentListModel,
  CommentListParamsSchema,
  CommentModel,
  CreateCommentParamsSchema,
  CreateCommentResponseSchema,
  ResolveCommentParamsSchema,
  ShareCommentParamsSchema,
  UpdateCommentParamsSchema,
  type commentBulkUpdateStatusActionType,
  type commentKeyType,
  commentReadStatusType,
} from '../models/comment';
import { commentQueryKeyMap } from '../utils/query-keys';

const API_BASE_URL = '/api/comments/';

export const useCommentList = <U = z.infer<typeof CommentListModel>>({
  params,
  select,
  enabled = true,
  retry,
}: {
  params?: z.infer<typeof CommentListParamsSchema>;
  select?: (data: z.infer<typeof CommentListModel>) => U;
  enabled?: boolean;
  retry?: boolean | number;
}) => {
  const parsedParams = params
    ? CommentListParamsSchema.parse({
        ...params,
        tagIds: params.tagIds?.at(0) === 'null' ? [] : params.tagIds,
      })
    : {};
  const { assigneeId, ...payload } = parsedParams;

  return useQuery({
    queryKey: commentQueryKeyMap.commentList(...(params ? [params] : [])),
    queryFn: async () => {
      const res = await axiosGet(API_BASE_URL, {
        ...payload,
        assigneeIds: assigneeId,
        tagIds: payload?.tagIds?.join(','),
        readStatuses: params?.readStatus
          ? (params.readStatus === 'all'
              ? commentReadStatusType.options
              : params.readStatus === 'read'
                ? [
                    commentReadStatusType.Enum.read,
                    commentReadStatusType.Enum.unknown,
                  ]
                : [commentReadStatusType.Enum.unread]
            ).join(',')
          : undefined,
      });
      return CommentListModel.parse(res);
    },
    select,
    enabled,
    retry,
  });
};

export const useComment = (id: string) => {
  return useQuery({
    queryKey: commentQueryKeyMap.comment(id),
    queryFn: async () => {
      const res = await axiosGet(`${API_BASE_URL}${id}/`);
      return CommentModel.parse(res);
    },
    enabled: !!id,
  });
};

export const useCreateComment = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async (values: z.infer<typeof CreateCommentParamsSchema>) => {
      const payload = CreateCommentParamsSchema.parse(values);
      const res = await axiosPost(API_BASE_URL, payload);
      return CreateCommentResponseSchema.parse(res);
    },
    onSuccess: async (_, { key, objectId }) => {
      const inputCandidateParams = { key, objectId, preset: true };

      await Promise.all([
        invalidate(commentQueryKeyMap.commentList()),
        invalidate(
          commentQueryKeyMap.commentInputCandidateUsers(inputCandidateParams),
        ),
        invalidate(
          commentQueryKeyMap.commentInputCandidateOrganizations(
            inputCandidateParams,
          ),
        ),
        invalidate(authenticationQueryKeyMap.myNotificationBadges()),
        invalidate(projectQueryKeyMap.projectNotification()),
      ]);
    },
  });
};

export const useUpdateComment = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async (params: z.infer<typeof UpdateCommentParamsSchema>) => {
      const { id, ...payload } = UpdateCommentParamsSchema.parse(params);
      return await axiosPatch(`${API_BASE_URL}${id}/`, payload);
    },
    onSuccess: async (_, { id }) => {
      await Promise.all([
        invalidate(commentQueryKeyMap.commentList()),
        invalidate(commentQueryKeyMap.comment(id)),
        invalidate(authenticationQueryKeyMap.myNotificationBadges()),
      ]);
    },
  });
};

export const useResolveComment = (id: string) => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async (params: z.infer<typeof ResolveCommentParamsSchema>) => {
      const payload = ResolveCommentParamsSchema.parse(params);
      return await axiosPatch(`${API_BASE_URL}${id}/`, payload);
    },
    onSuccess: async () => {
      await Promise.all([
        invalidate(commentQueryKeyMap.commentList()),
        invalidate(commentQueryKeyMap.comment(id)),
        invalidate(authenticationQueryKeyMap.myNotificationBadges()),
        invalidate(projectQueryKeyMap.projectNotification()),
      ]);
    },
  });
};

export const useDeleteComment = ({
  target,
}: {
  target: {
    key: z.infer<typeof commentKeyType>;
    id: string;
  };
}) => {
  const { invalidate } = useInvalidateQuery();

  return useMutation({
    mutationFn: async (id: string) => {
      return await axiosDelete(`${API_BASE_URL}${id}/`);
    },
    onSuccess: async (_) => {
      await Promise.all([
        invalidate(commentQueryKeyMap.commentList()),
        invalidate(authenticationQueryKeyMap.myNotificationBadges()),
        ...(() => {
          switch (target.key) {
            case 'project':
              return [
                invalidate(projectQueryKeyMap.projectNotification(target.id)),
              ];
            default:
              return [];
          }
        })(),
      ]);
    },
  });
};

export const useShareComment = () => {
  const { invalidate } = useInvalidateQuery();

  return useMutation({
    mutationFn: async ({
      id,
      ...values
    }: z.infer<typeof ShareCommentParamsSchema> & { id: string }) => {
      const payload = ShareCommentParamsSchema.parse(values);
      await axiosPatch(`${API_BASE_URL}${id}/shares/`, payload);
    },
    onSuccess: async (_, { id }) => {
      await Promise.all([
        invalidate(commentQueryKeyMap.comment(id)),
        invalidate(commentQueryKeyMap.commentList()),
      ]);
    },
  });
};

export const useBulkUpdateCommentsStatus = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async ({
      action,
      commentIds,
    }: {
      action: z.infer<typeof commentBulkUpdateStatusActionType>;
      commentIds: string[];
    }) => {
      const res = await axiosPost(`${API_BASE_URL}bulk-change-read-status/`, {
        action,
        comments: commentIds,
      });

      return BulkUpdateStatusBaseModel.parse(res);
    },
    onSuccess: async () => {
      await Promise.all([
        invalidate(commentQueryKeyMap.comment()),
        invalidate(commentQueryKeyMap.commentList()),
        invalidate(authenticationQueryKeyMap.myNotificationBadges()),
      ]);
    },
  });
};

export const useChangeCommentReadStatus = (id: string) => {
  const { invalidate } = useInvalidateQuery();

  return useMutation({
    mutationFn: async ({
      ...params
    }: {
      readStatus: z.infer<typeof commentReadStatusType>;
    }) => {
      return await axiosPost(
        `${API_BASE_URL}${id}/change-read-status/`,
        params,
      );
    },
    onSuccess: async () => {
      await Promise.all([
        invalidate(commentQueryKeyMap.commentList()),
        invalidate(commentQueryKeyMap.comment(id)),
        invalidate(authenticationQueryKeyMap.myNotificationBadges()),
        invalidate(projectQueryKeyMap.projectNotification()),
      ]);
    },
  });
};
